//#region src/tunnel-status.ts
/**
* The answers of the tunnel terminator itself (plan 2026-10-27-cp-dp-network-path, WS-2,
* overview §10a rule 5).
*
* A request through the terminator gets one of two kinds of answer: the answer of the far end (core-api on the
* DP, or the CP API), or a tunnel status that the terminator writes when it cannot carry the
* request. A tunnel status carries {@link TUNNEL_STATUS_HEADER}, so a caller can tell "the
* terminator could not reach the far end" from "the far end answered 503". §10a rule 7 needs the
* difference: a terminator fault must not read as a DP outage.
*
* The terminator (WS-3) writes each status with {@link tunnelStatusResponse} as the form. It
* removes {@link TUNNEL_STATUS_HEADER} from every answer it forwards, so a far end cannot forge
* a tunnel status. The CP clients (WS-6, WS-7, WS-8) and the DP agent read an answer with
* {@link readTunnelStatus}.
*
* Browser-safe: no Node builtin and no Node global. Every function here is pure.
*/
/**
* The four tunnel status codes.
*
* - `tunnel_down`: a CP-to-DP request names a data plane whose tunnel the terminator does not
*   hold, or the tunnel closed before the DP sent its response headers.
* - `cp_unavailable`: a DP-to-CP request (the operator poll, a status post, a cert renewal, an OTLP
*   push) arrived on a live tunnel, and the CP service behind the terminator refused the
*   connection or did not answer. The terminator keeps the tunnel open (§10a rule 5).
* - `busy`: the request would exceed the stream limit of its data plane (8 streams per DP,
*   decision 6). The accept limit of decision 6 acts on new tunnel connections, before any HTTP
*   exchange, so it gives no `busy` answer.
* - `too_large`: a request body or a response body is larger than {@link TUNNEL_BODY_CAP_BYTES}.
*/
const TUNNEL_STATUS_CODES = [
	"tunnel_down",
	"cp_unavailable",
	"busy",
	"too_large"
];
/** True when `value` is exactly one of {@link TUNNEL_STATUS_CODES}. */
function isTunnelStatusCode(value) {
	return TUNNEL_STATUS_CODES.includes(value);
}
/**
* The response header that marks an answer of the terminator. Its value is one
* {@link TunnelStatusCode}. Lower case, as Node and fetch give header names.
*/
const TUNNEL_STATUS_HEADER = "x-zion-tunnel-status";
/**
* The HTTP status of each tunnel status code.
*
* - `tunnel_down` and `cp_unavailable`: 503, the far end is unreachable for now.
* - `busy`: 429, the caller sends more than its limit allows.
* - `too_large`: 413, for a request body and for a response body alike. A 4xx status keeps a
*   generic retry of 5xx answers from sending the same body again.
*/
const TUNNEL_STATUS_HTTP_STATUS = Object.freeze({
	tunnel_down: 503,
	cp_unavailable: 503,
	busy: 429,
	too_large: 413
});
/**
* The largest request or response body the terminator carries: 16 MiB, 16,777,216 bytes. The
* defaults row "one cap, one timeout chain" sets it until plan 2026-10-27-dp-telemetry-residency
* settles its cap; that plan's R-027 names the same 16 MiB.
*/
const TUNNEL_BODY_CAP_BYTES = 16777216;
/**
* True when a caller may send the same request again after a backoff.
*
* Callers: the CP clients and the DP agent and operator, after {@link readTunnelStatus} answered a
* code, before they send the request a second time.
*
* Input: `code`; `request.idempotent`, true when two copies of the request have the effect of one
* copy, for example a `GET`, a `PUT` of a whole value or a `DELETE`. A `POST` that makes a row or a
* token is not idempotent. Output per code:
* - `busy`: true. The terminator refused the request at the stream limit, before it sent the
*   request to the far end, so the far end got no copy.
* - `tunnel_down` and `cp_unavailable`: `request.idempotent`. The far end can have received and
*   applied the request before the tunnel closed or before the CP service stopped its answer. A
*   second copy of a request that is not idempotent can then apply it twice.
* - `too_large`: false. The same body gets the same answer. For a response body, the far end
*   already applied the request.
*
* Pure.
*/
function isRetryableTunnelStatus(code, request) {
	if (code === "too_large") return false;
	return code === "busy" || request.idempotent;
}
/** The most characters (UTF-16 code units) of the `message` of a tunnel status body. */
const TUNNEL_STATUS_MESSAGE_MAX = 512;
/** The text of each code when the caller of {@link tunnelStatusResponse} gives none. */
const DEFAULT_MESSAGES = {
	tunnel_down: "The terminator holds no live tunnel for this data plane.",
	cp_unavailable: "The control plane service behind the terminator did not answer.",
	busy: "The tunnel of this data plane is at its stream limit.",
	too_large: `The body is larger than the tunnel cap of ${TUNNEL_BODY_CAP_BYTES} bytes.`
};
/**
* The answer the terminator writes for `code`.
*
* Callers: the terminator configuration of WS-3 takes it as the form, and the Fakes of the CP
* clients and of the DP agent return it.
*
* Input: `code`; `message`, an optional text of 1 to 512 characters, else the default text of the
* code. Output: the HTTP status of {@link TUNNEL_STATUS_HTTP_STATUS}; the headers `content-type:
* application/json` and {@link TUNNEL_STATUS_HEADER} set to `code`; the body
* `{"error":<code>,"message":<text>}`, which parses under `TunnelStatusBodySchema`.
*
* Errors: throws `RangeError` for a `message` that is empty or longer than
* {@link TUNNEL_STATUS_MESSAGE_MAX} characters. Pure.
*/
function tunnelStatusResponse(code, message) {
	const text = message ?? DEFAULT_MESSAGES[code];
	if (text.length < 1 || text.length > 512) throw new RangeError(`A tunnel status message must have 1 to 512 characters`);
	const body = {
		error: code,
		message: text
	};
	return {
		status: TUNNEL_STATUS_HTTP_STATUS[code],
		headers: {
			"content-type": "application/json",
			[TUNNEL_STATUS_HEADER]: code
		},
		body: JSON.stringify(body)
	};
}
/**
* The tunnel status of an answer, or `null` when the far end wrote the answer.
*
* Callers: the CP clients after each call through the tunnel, and the DP agent and operator after
* each call to the CP, before they read the body.
*
* Input: the status and headers of an answer. Output: the code when {@link TUNNEL_STATUS_HEADER}
* holds exactly one {@link TunnelStatusCode} and the HTTP status equals the status of that code in
* {@link TUNNEL_STATUS_HTTP_STATUS}. `null` in every other case: no header, a value that is not a
* code (another case, white space, two values joined with `,`), or a status that does not match the
* code. A `null` answer is the answer of the far end, and the caller handles it as today. Pure.
*/
function readTunnelStatus(response) {
	const marker = response.headers.get(TUNNEL_STATUS_HEADER);
	if (marker === null || !isTunnelStatusCode(marker)) return null;
	return TUNNEL_STATUS_HTTP_STATUS[marker] === response.status ? marker : null;
}
//#endregion
export { TUNNEL_BODY_CAP_BYTES, TUNNEL_STATUS_CODES, TUNNEL_STATUS_HEADER, TUNNEL_STATUS_HTTP_STATUS, TUNNEL_STATUS_MESSAGE_MAX, isRetryableTunnelStatus, readTunnelStatus, tunnelStatusResponse };

//# sourceMappingURL=tunnel-status.js.map